import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAnomalies, useAckAnomaly, useAckAnomaliesBulk } from '../lib/api';
import StatusBadge from '../components/StatusBadge.jsx';
import { formatDistanceToNow } from 'date-fns';
import clsx from 'clsx';

const EVENT_LABELS = {
  duplicate_quote_suppressed:     { label: 'Duplicate quote suppressed', tone: 'red',   icon: '🚨', short: 'Bug #36 safety net' },
  decline_blocked_already_posted: { label: 'Decline blocked',            tone: 'red',   icon: '🚨', short: 'S74 guard — quote already posted' },
  decline_failed_button_missing:  { label: 'Decline button missing',     tone: 'red',   icon: '🚨', short: 'S74 phase 4 fallback' },
  trip_reset_for_reextraction:    { label: 'Trip reset',                 tone: 'amber', icon: 'ℹ️', short: '/reset issued' },
};

function fmtTime(iso) {
  if (!iso) return '—';
  try {
    // S80: see LiveOps.jsx fmtTime — server returns SQLite space-format timestamps
    // which Chrome parses as local time. Normalize space-format to ISO-Z so they're
    // parsed as UTC.
    const norm = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(iso)
      ? iso.replace(' ', 'T') + 'Z'
      : iso;
    return formatDistanceToNow(new Date(norm), { addSuffix: true });
  } catch (_) { return iso; }
}

function bodyFor(eventType, details) {
  const d = details || {};
  switch (eventType) {
    case 'duplicate_quote_suppressed':
      return `Tail ${d.tail || '?'} · $${d.price ?? '?'} · existing Q${d.existing_quote ?? '?'} blocks new Q${d.new_quote ?? '?'}`;
    case 'decline_blocked_already_posted':
      return `Blocked quote: Q${d.blocked_quote ?? '?'}`;
    case 'decline_failed_button_missing': {
      const tails = Array.isArray(d.tails) ? d.tails.join(', ') : (d.tails || '?');
      return `Operator ${d.operator_id || '?'} · Tails: ${tails}`;
    }
    case 'trip_reset_for_reextraction':
      return d.reason || d.actor || '';
    default:
      return JSON.stringify(d).slice(0, 200);
  }
}

function EventRow({ ev, onAck, selected, onToggleSelect }) {
  const cfg = EVENT_LABELS[ev.event_type] || { label: ev.event_type, tone: 'gray', icon: '⚠️', short: '' };
  return (
    <tr className={clsx('border-t', ev.acknowledged && 'opacity-50')}>
      <td className="py-2 pr-2 align-top w-8">
        {!ev.acknowledged && (
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onToggleSelect(ev.rowid)}
            aria-label={`Select event ${ev.rowid}`}
            className="cursor-pointer"
          />
        )}
      </td>
      <td className="py-2 pr-2 align-top">
        <div className="flex items-start gap-2">
          <span className="text-lg leading-5">{cfg.icon}</span>
          <div>
            <div className="font-semibold text-sm">{cfg.label}</div>
            <div className="text-xs text-slate-500">{cfg.short}</div>
          </div>
        </div>
      </td>
      <td className="py-2 pr-2 align-top text-sm">
        {/* S98: carry operator_id when present — trip_id collides across operators */}
        {ev.resource_id ? (
          <Link to={`/trips/${encodeURIComponent(ev.resource_id)}${ev.operator_id ? `?operator=${encodeURIComponent(ev.operator_id)}` : ''}`} className="font-mono text-tint hover:underline">
            {ev.resource_id}
          </Link>
        ) : '—'}
      </td>
      <td className="py-2 pr-2 align-top text-xs text-slate-600">
        {bodyFor(ev.event_type, ev.details)}
      </td>
      <td className="py-2 pr-2 align-top text-xs text-slate-500 whitespace-nowrap">
        <div>{fmtTime(ev.created_at)}</div>
        <div className="font-mono text-[10px]">{ev.actor || ''}</div>
      </td>
      <td className="py-2 align-top text-right">
        {ev.acknowledged ? (
          <div className="text-xs text-slate-500">
            <div>✓ ack {fmtTime(ev.acknowledged_at)}</div>
            <div className="font-mono text-[10px]">{ev.acknowledged_by || ''}</div>
            <button
              onClick={() => onAck(ev.rowid, true)}
              className="text-[10px] text-slate-400 hover:text-slate-700 underline"
            >unack</button>
          </div>
        ) : (
          <button
            onClick={() => onAck(ev.rowid, false)}
            className="px-3 py-1 text-xs rounded bg-tint text-on-tint hover:opacity-90 whitespace-nowrap"
          >
            <span className="hidden sm:inline">Acknowledge</span>
            <span className="sm:hidden">Ack ✓</span>
          </button>
        )}
      </td>
    </tr>
  );
}

export default function Anomalies() {
  const [since, setSince] = useState('24h');
  const [eventType, setEventType] = useState('');
  const [acked, setAcked] = useState('false');
  const [selected, setSelected] = useState(new Set());

  const { data, isLoading, isError, error } = useAnomalies({
    since,
    event_type: eventType || null,
    acked,
  });
  const ackMutation = useAckAnomaly();
  const bulkAckMutation = useAckAnomaliesBulk();

  // Selectable rows = those still un-acked. Acked rows can't be re-acked.
  const selectableRowids = useMemo(
    () => (data?.events || []).filter(e => !e.acknowledged).map(e => e.rowid),
    [data]
  );

  const allSelected = selectableRowids.length > 0 && selectableRowids.every(r => selected.has(r));
  const someSelected = selected.size > 0;

  function handleAck(rowid, unack) {
    ackMutation.mutate({ rowid, unack });
    setSelected(prev => {
      const next = new Set(prev);
      next.delete(rowid);
      return next;
    });
  }

  function toggleSelect(rowid) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(rowid)) next.delete(rowid); else next.add(rowid);
      return next;
    });
  }

  function toggleSelectAll() {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(selectableRowids));
    }
  }

  function handleBulkAck() {
    const rowids = Array.from(selected);
    if (rowids.length === 0) return;
    if (rowids.length > 1 && !window.confirm(`Acknowledge ${rowids.length} events?`)) return;
    bulkAckMutation.mutate(
      { rowids, unack: false },
      { onSuccess: () => setSelected(new Set()) }
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">Anomaly Inbox</h1>
        <p className="text-sm text-slate-600">
          Events the alerter watches. Acknowledge to stop further Telegram pings (won't recall ones already sent).
        </p>
      </div>

      {/* Filters */}
      <div className="card p-3 flex flex-wrap gap-3 items-center text-sm">
        <label className="flex items-center gap-2">
          <span className="text-slate-600">Window:</span>
          <select value={since} onChange={e => setSince(e.target.value)}
                  className="border rounded px-2 py-1">
            <option value="24h">Last 24h</option>
            <option value="7d">Last 7d</option>
            <option value="30d">Last 30d</option>
            <option value="all">All time</option>
          </select>
        </label>
        <label className="flex items-center gap-2">
          <span className="text-slate-600">Type:</span>
          <select value={eventType} onChange={e => setEventType(e.target.value)}
                  className="border rounded px-2 py-1">
            <option value="">All types</option>
            {Object.entries(EVENT_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2">
          <span className="text-slate-600">Show:</span>
          <select value={acked} onChange={e => setAcked(e.target.value)}
                  className="border rounded px-2 py-1">
            <option value="false">Un-acked only</option>
            <option value="true">Acked only</option>
            <option value="all">All</option>
          </select>
        </label>
      </div>

      {/* Counts summary */}
      {data?.counts && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
          {Object.entries(EVENT_LABELS).map(([k, cfg]) => {
            const c = data.counts[k] || { unacked: 0, acked: 0, total: 0 };
            return (
              <div key={k} className="card p-3">
                <div className="text-xs text-slate-500">{cfg.icon} {cfg.label}</div>
                <div className="font-semibold text-lg">
                  {c.unacked} <span className="text-sm text-slate-400 font-normal">un-acked</span>
                </div>
                <div className="text-xs text-slate-500">{c.total} total ({c.acked} acked)</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Bulk-action bar — appears when at least one row is selected */}
      {someSelected && (
        <div className="card p-3 flex items-center justify-between gap-3 text-sm bg-tint-fill border border-separator">
          <div>
            <strong>{selected.size}</strong> selected
            <button
              onClick={() => setSelected(new Set())}
              className="ml-3 text-xs text-slate-600 hover:text-slate-900 underline"
            >Clear</button>
          </div>
          <button
            onClick={handleBulkAck}
            disabled={bulkAckMutation.isPending}
            className="px-4 py-1.5 rounded bg-tint text-on-tint hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {bulkAckMutation.isPending ? 'Acknowledging…' : `Acknowledge ${selected.size}`}
          </button>
        </div>
      )}

      {/* Table */}
      {isLoading && <div className="text-slate-500 text-sm">Loading…</div>}
      {isError && <div className="text-rose-700 text-sm">Error: {error.message}</div>}
      {!isLoading && !isError && data && (
        data.events.length === 0
          ? <div className="card p-6 text-center text-slate-500 text-sm">No matching anomalies.</div>
          : (
            <div className="card overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr className="text-left text-xs text-slate-500">
                    <th className="py-2 px-2 w-8">
                      {selectableRowids.length > 0 && (
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={toggleSelectAll}
                          aria-label="Select all un-acked"
                          className="cursor-pointer"
                        />
                      )}
                    </th>
                    <th className="py-2 px-2">Event</th>
                    <th className="py-2 px-2">Trip</th>
                    <th className="py-2 px-2">Details</th>
                    <th className="py-2 px-2">When / Actor</th>
                    <th className="py-2 px-2 text-right">Ack</th>
                  </tr>
                </thead>
                <tbody>
                  {data.events.map(ev => (
                    <EventRow
                      key={ev.rowid}
                      ev={ev}
                      onAck={handleAck}
                      selected={selected.has(ev.rowid)}
                      onToggleSelect={toggleSelect}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )
      )}
    </div>
  );
}
